import React, { useEffect, useRef, useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { CircularProgress, Typography, useTheme } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import { BasicModalContent, uploadFilesRequest } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { useTranslation } from "react-i18next";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/system";
import _ from "lodash";
import { InstalledBaseNodeEventContext } from "../../graphql/queries/installedBaseNodeEventContext";
import { CreateInstalledBaseEvent } from "../../graphql/mutations/createInstalledBaseEvent";
import { AddFilesToInstalledBaseNode } from "../../graphql/mutations/addFilesToInstalledBaseNode";
import { UpdateInstalledBaseNode } from "../../graphql/mutations/updateInstalledBaseNode";
import { NODE_IMAGE_REFETCH_QUERIES } from "../../utils/nodeImage";
import InstalledBaseEventForm, { InstalledBaseEventFormVariables } from "../InstalledBaseEventForm";
import { InstalledBaseNodeTypeKey, ManualEventType } from "../../codegen/graphql";
import { filterAvailableEvents } from "../../utils/filterAvailableEvents";
import { toFiniteNumber } from "../../utils/moveWizardSync";
import { useEventTypeLabels } from "../../utils/eventTypeLabels";
import { getEventTypeIcon } from "../../utils/eventTypeIcons";
import { IotSystemMatch, resolveIotSystemForNodes } from "../../utils/findIotSystemInNodePath";
import EventFollowUpPrompt, { EventFollowUp } from "./EventFollowUpPrompt";
import { buildIotFollowUp } from "./EventIotUpdatePrompt";
import { isContaminationDeclarationsEnabled } from "../../utils/contaminationDeclarationsEnabled";
import { shouldOfferContaminationDeclaration } from "../../utils/contaminationDeclaration/followUps";
import { resolveLatestRemoveEventId } from "../../utils/contaminationDeclaration/resolveLatestRemoveEventId";
import { resolveDeclarationAvailability } from "../../utils/contaminationDeclaration/resolveDeclarationAvailability";
import { useContaminationDeclarationAccess } from "../../hooks/useContaminationDeclarationAccess";
import { useOpenContaminationDeclaration } from "../../hooks/useOpenContaminationDeclaration";

const DEFAULT_REFETCH = ["AssetBreadcrumbPath", "AssetNameplateInfo", "InstalledBaseEventList", "AssetRemoveEvents"];

// Events that move the asset within the installed base and may therefore invalidate
// the IoT configuration of the system the asset leaves or joins.
const PATH_CHANGING_EVENT_TYPES: ManualEventType[] = [
    ManualEventType.ASSET_INSTALL,
    ManualEventType.ASSET_EXCHANGE,
    ManualEventType.ASSET_REMOVE,
    ManualEventType.ASSET_DELETE,
];

export interface InstalledBaseEventSuccessInfo {
    eventId: string;
    assetId: string;
    assetLocationId?: string;
    installedBaseNodeId?: string;
}

// Follow-ups the just-created event makes advisable; the modal stays open and
// offers them instead of closing (see EventFollowUpPrompt).
interface FollowUpState {
    iotSystem: IotSystemMatch | null;
    /** Offer to start a declaration of contamination for the removed asset. */
    offerDeclaration: boolean;
    /**
     * The ASSET_REMOVE event the declaration attaches to — on an exchange the
     * *removed* asset's. Resolved while deciding whether to offer at all,
     * because availability is asked per event, so the id has to be known
     * before the prompt renders rather than on click.
     */
    removeEventId: string | null;
}

interface Props {
    assetId: string;
    assetLocationId?: string;
    installedBaseNodeId: string | null;
    open: boolean;
    onClose: () => void;
    refetchQueries?: string[];
    initialEventType?: ManualEventType;
    /** Fired after a successful event creation. The install-from-register flow uses
     *  this to surface the resulting installedBaseNode id back to the tree-column. */
    onSuccess?: (info: InstalledBaseEventSuccessInfo) => void;
    /** The supplied `installedBaseNodeId` IS the install destination (e.g. sub-asset
     *  creation below an asset node) — suppresses the ASSET-typed heuristics that
     *  would otherwise force the destination picker / walk up to the parent location. */
    nodeIsInstallTarget?: boolean;
}

const CreateInstalledBaseEventModal = ({
    assetId,
    assetLocationId,
    installedBaseNodeId,
    open,
    onClose,
    refetchQueries,
    initialEventType,
    onSuccess,
    nodeIsInstallTarget,
}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const eventTypeLabels = useEventTypeLabels();
    const [currentEventType, setCurrentEventType] = useState<ManualEventType | undefined>(initialEventType);
    // Bumped whenever the modal transitions to open, so the underlying form fully remounts
    // and any prior step values / dropped files are wiped.
    const [formResetKey, setFormResetKey] = useState(0);
    // When the just-created event touches an IoT system or removes an asset, the modal
    // stays open and shows the follow-up prompt instead of closing. The post-success
    // side-effects (cache eviction, tab-switch dispatch, onSuccess) are deferred into
    // `finalizeRef` until the user dismisses the prompt — firing them early would
    // navigate the asset tab away and unmount this modal mid-prompt.
    const [followUpPrompt, setFollowUpPrompt] = useState<FollowUpState | null>(null);
    const finalizeRef = useRef<((includeTabSwitch: boolean) => void) | null>(null);
    const { canCreate: canCreateDeclaration } = useContaminationDeclarationAccess();
    const { openForEvent: openDeclarationForEvent, busy: declarationBusy } = useOpenContaminationDeclaration();

    useEffect(() => {
        if (open) {
            setFormResetKey((k) => k + 1);
            setCurrentEventType(initialEventType);
            setFollowUpPrompt(null);
            finalizeRef.current = null;
        }
    }, [open, initialEventType]);

    // Install from the register-asset flow: no node yet, the user picks it inside the form.
    const isInstallWithoutNode = !installedBaseNodeId && initialEventType === ManualEventType.ASSET_INSTALL;
    // Install into a known destination (sub-asset flow): the event type is fixed.
    // The destination's own event context lists events for ITS asset (maintenance,
    // exchange, …) and must not steer — or fall back — this modal's event type.
    const isInstallIntoNode = !!nodeIsInstallTarget && initialEventType === ManualEventType.ASSET_INSTALL;

    const { data, loading } = useQuery(InstalledBaseNodeEventContext, {
        variables: { id: installedBaseNodeId },
        skip: !installedBaseNodeId,
    });

    const [createInstalledBaseEventMutation] = useMutation(CreateInstalledBaseEvent);
    const [addFilesToInstalledBaseNodeMutation] = useMutation(AddFilesToInstalledBaseNode);
    // Install flow: sets the marked upload as the new asset node's image.
    const [updateInstalledBaseNodeMutation] = useMutation(UpdateInstalledBaseNode);
    const [submitting, setSubmitting] = useState(false);
    const apolloClient = useApolloClient();

    const availableEvents: ManualEventType[] = (isInstallWithoutNode || isInstallIntoNode)
        ? [ManualEventType.ASSET_INSTALL]
        : filterAvailableEvents(
            _.get(data, 'installedBaseNode.availableInstalledBaseEvents', []),
            _.get(data, 'installedBaseNode.assetLocation.isInstalled'),
        );

    const formInitialEventType: ManualEventType | undefined =
        initialEventType && availableEvents.includes(initialEventType)
            ? initialEventType
            : availableEvents[0];

    // The mutation's `targetNodeId` semantics depend on what `installedBaseNodeId`
    // points at:
    //   - install-without-node flow → `variables.targetedNode` (user-picker value)
    //   - the supplied node is itself ASSET-typed (e.g. opened from an asset detail
    //     for exchange/remove/maintenance) → walk up to the asset's parent location
    //   - the supplied node is a regular location (CLIENT, SYSTEM, etc., e.g. opened
    //     from the installed-base tree to install an asset there) → use it directly
    //   - EXCEPT with `nodeIsInstallTarget`: the ASSET-typed node is itself the
    //     destination (sub-asset creation below an asset) → use it directly too
    const parentNodeId = _.get(data, 'installedBaseNode.parentNode.id') as string | undefined;
    const nodeTypeKey = _.get(data, 'installedBaseNode.nodeType.key') as InstalledBaseNodeTypeKey | undefined;
    // Relocation (REMOVE / EXCHANGE) must stay within the node's own client tree.
    const clientRootId = _.get(data, 'installedBaseNode.pathNodes[0].id') as string | undefined;
    const averageRuntimePerDay = toFiniteNumber(_.get(data, 'installedBaseNode.assetLocation.averageRuntimePerDay'));

    const handleSubmit = async (variables: InstalledBaseEventFormVariables) => {
        const effectiveRefetch = refetchQueries || DEFAULT_REFETCH;
        if (!variables.targetedNode) {
            toast.error(t("Please select an installation target."));
            return;
        }
        // For an install whose target isn't a concrete location (no node, or the
        // asset's own ASSET-typed node), the form's picker value IS the install
        // target. Otherwise: an asset-typed node walks up to its parent location —
        // unless it is the destination itself (`nodeIsInstallTarget`, sub-asset
        // creation); a regular location node (installed-base tree) is used directly.
        const isInstallTargetPick = variables.eventType === ManualEventType.ASSET_INSTALL
            && (!installedBaseNodeId || (nodeTypeKey === InstalledBaseNodeTypeKey.ASSET && !nodeIsInstallTarget));
        const mutationTargetNodeId = isInstallTargetPick
            ? variables.targetedNode
            : nodeTypeKey === InstalledBaseNodeTypeKey.ASSET && !nodeIsInstallTarget
                ? parentNodeId
                : installedBaseNodeId;
        if (!mutationTargetNodeId) {
            toast.error(t("Cannot create event: no parent node found for this installation."));
            return;
        }
        setSubmitting(true);
        try {
            const res = await createInstalledBaseEventMutation({
                variables: {
                    event: {
                        assetId: variables.assetId!,
                        eventType: variables.eventType,
                        targetNodeId: mutationTargetNodeId,
                        // ASSET_DELETE always lands in the trash — the server picks
                        // the destination and rejects a supplied relocation target.
                        relocationNodeId: variables.eventType === ManualEventType.ASSET_DELETE
                            ? undefined
                            : variables.relocationNode,
                        replacementAssetId: variables.replacementAssetId,
                    },
                    attributes: variables.attributes,
                },
                // Defer cache refetches until files are attached, so the event list refreshes
                // once with the final state instead of mid-flight.
                refetchQueries: variables.files.length === 0 ? effectiveRefetch : undefined,
            });

            const eventId = res.data?.createInstalledBaseEvent?.id;
            const eventInstalledBaseNodeId = res.data?.createInstalledBaseEvent?.installedBaseNode?.id;
            const newAssetLocationId = res.data?.createInstalledBaseEvent?.assetLocation?.id;
            const newInstalledBaseNodeId = res.data?.createInstalledBaseEvent?.assetLocation?.installedBaseNode?.id;
            if (!eventId) {
                // Partial-data edge: the mutation resolved without throwing but
                // returned no event — surface the backend's reason if it sent one.
                const resErrors = (res as any)?.errors;
                toast.error(resErrors?.length
                    ? resolveErrorMessage({ graphQLErrors: resErrors }, t)
                    : t("Error creating event."));
                return;
            }

            if (variables.files.length > 0) {
                if (!eventInstalledBaseNodeId) {
                    toast.error(t("Event created but uploading files failed."));
                    onClose();
                    return;
                }
                try {
                    const uploaded = await uploadFilesRequest(variables.files, keycloak.token);
                    await addFilesToInstalledBaseNodeMutation({
                        variables: {
                            installedBaseNodeId: eventInstalledBaseNodeId,
                            input: uploaded.map((f: { fileId: string; filename: string }) => ({
                                fileServiceUuid: f.fileId,
                                filename: f.filename,
                                category: "Event Files",
                                eventId,
                            })),
                        },
                        refetchQueries: effectiveRefetch,
                    });

                    // Install flow: the image the user marked becomes the asset node's
                    // image. `imageId` is the file-service UUID; the backend links the
                    // node file just attached above (no duplicate). The event's node IS
                    // the asset node, and the update input needs its key + title.
                    // Failing here must not fail the (already created) event.
                    const markedUpload = variables.eventType === ManualEventType.ASSET_INSTALL
                        && variables.assetImageIndex != null
                        ? uploaded[variables.assetImageIndex]
                        : undefined;
                    const eventNode = res.data?.createInstalledBaseEvent?.installedBaseNode;
                    if (markedUpload?.fileId && eventNode?.key && eventNode?.title) {
                        try {
                            await updateInstalledBaseNodeMutation({
                                variables: {
                                    id: eventInstalledBaseNodeId,
                                    input: { key: eventNode.key, title: eventNode.title, imageId: markedUpload.fileId },
                                },
                                refetchQueries: NODE_IMAGE_REFETCH_QUERIES,
                            });
                        } catch (imageErr) {
                            toast.warning(t("Event created, but the asset image could not be set."));
                        }
                    }
                } catch (fileErr) {
                    toast.error(t("Event created but uploading files failed."));
                    onClose();
                    return;
                }
            }

            toast.success(t("Successfully created event"));

            // The post-success side-effects. When an IoT-system prompt is shown these
            // are deferred until the user dismisses it (see below); `includeTabSwitch`
            // is skipped when we're about to navigate to the system page so the two
            // navigations don't fight.
            const finalize = (includeTabSwitch: boolean) => {
                if (newAssetLocationId && assetLocationId && newAssetLocationId !== assetLocationId) {
                    // Drop cached entities for both locations + the new installed-base
                    // node so the newly opened tab queries fresh data instead of serving
                    // possibly-stale state from prior visits.
                    [assetLocationId, newAssetLocationId].forEach((id) => {
                        apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'AssetLocation', id }) });
                    });
                    if (newInstalledBaseNodeId) {
                        apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'InstalledBaseNode', id: newInstalledBaseNodeId }) });
                    }
                    apolloClient.cache.gc();
                    if (includeTabSwitch) {
                        window.dispatchEvent(new CustomEvent('asset-tab-switch', {
                            detail: { from: assetLocationId, to: newAssetLocationId },
                        }));
                    }
                }
                onSuccess?.({
                    eventId,
                    assetId: variables.assetId!,
                    assetLocationId: newAssetLocationId,
                    installedBaseNodeId: newInstalledBaseNodeId,
                });
            };

            // For path-changing events, check whether the asset's new (relocation /
            // install target) or old (target) location sits under an IoT system. The
            // new location takes priority. A removal / exchange additionally offers to
            // start a declaration of contamination for the removed asset. If anything
            // applies, keep the modal open and offer the follow-ups instead of closing.
            const iotSystem = PATH_CHANGING_EVENT_TYPES.includes(variables.eventType)
                ? await resolveIotSystemForNodes(apolloClient, [variables.relocationNode, mutationTargetNodeId])
                : null;
            // The declaration is currently US-only, gated per event on the owning client's
            // region. That question is asked of the ASSET_REMOVE event, which
            // `createInstalledBaseEvent` did not return (it returns the relocation-side one), so
            // the id has to be resolved here rather than lazily on click as it used to be. Both
            // lookups are paid for only once the free half of the decision already says yes, so
            // no other event type pays for them.
            const offerContext = {
                eventType: variables.eventType,
                canCreate: canCreateDeclaration,
                enabled: isContaminationDeclarationsEnabled(),
            };
            const offeredIfAvailable = shouldOfferContaminationDeclaration({ ...offerContext, available: true });
            const removeEventId = offeredIfAvailable
                ? await resolveLatestRemoveEventId(apolloClient, variables.assetId!)
                : null;
            const offerDeclaration = shouldOfferContaminationDeclaration({
                ...offerContext,
                available: !!removeEventId && await resolveDeclarationAvailability(apolloClient, removeEventId),
            });

            if (iotSystem || offerDeclaration) {
                finalizeRef.current = finalize;
                setFollowUpPrompt({ iotSystem, offerDeclaration, removeEventId });
                return;
            }

            finalize(true);
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    // Run the deferred post-success side-effects, then close. `includeTabSwitch` is
    // false when we're navigating away (system page, declaration editor) so the
    // asset-tab navigation and the follow-up navigation don't compete.
    const dismissFollowUpPrompt = (includeTabSwitch: boolean) => {
        finalizeRef.current?.(includeTabSwitch);
        finalizeRef.current = null;
        setFollowUpPrompt(null);
        onClose();
    };

    const handleIotUpdate = () => {
        const systemId = followUpPrompt?.iotSystem?.systemId;
        dismissFollowUpPrompt(false);
        if (systemId) {
            navigate(`/systems/${systemId}/iotEdit`);
        }
    };

    // The ASSET_REMOVE event was already resolved while deciding whether to offer this
    // follow-up at all — the prompt is only reachable when it was found and the form is
    // offered for it — so this opens the editor without looking it up a second time.
    const handleCreateDeclaration = async () => {
        const removeEventId = followUpPrompt?.removeEventId;
        if (!removeEventId) {
            toast.error(t("The removal event could not be found. You can create the declaration later from the event history."));
            dismissFollowUpPrompt(true);
            return;
        }
        await openDeclarationForEvent(removeEventId, { onBeforeNavigate: () => dismissFollowUpPrompt(false) });
    };

    const renderBody = () => {
        if (followUpPrompt) {
            const followUps: EventFollowUp[] = [];
            if (followUpPrompt.iotSystem) {
                followUps.push(buildIotFollowUp(t, followUpPrompt.iotSystem.systemTitle, handleIotUpdate));
            }
            if (followUpPrompt.offerDeclaration) {
                followUps.push({
                    key: "doc",
                    description: t("Create a Declaration of Contamination for the removed asset now? It is not mandatory – you can also create it later from the lifecycle event."),
                    actionLabel: t("Create declaration"),
                    onAction: handleCreateDeclaration,
                    loading: declarationBusy,
                    testId: "event-followup-doc",
                });
            }
            const onlyIot = followUps.length === 1 && !!followUpPrompt.iotSystem;
            const onlyDeclaration = followUps.length === 1 && followUpPrompt.offerDeclaration;
            return (
                <EventFollowUpPrompt
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    followUps={followUps}
                    onClose={() => dismissFollowUpPrompt(true)}
                    closeLabel={onlyIot ? t("Close without updating") : onlyDeclaration ? t("Not now") : t("Close")}
                />
            );
        }

        if (!installedBaseNodeId && !isInstallWithoutNode) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', p: 2 }}>
                    <Typography level="body-md" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        {t("Cannot create event: asset is not installed.")}
                    </Typography>
                </Box>
            );
        }

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!formInitialEventType) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', p: 2 }}>
                    <Typography level="body-md" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        {t("No events can be created for this asset.")}
                    </Typography>
                </Box>
            );
        }

        // When the caller pre-selects an event type (e.g. via the actions menu or a dashboard
        // shortcut), skip the synthetic event-type picker step entirely and drop the user
        // straight into the form for that type.
        const lockedEventType = initialEventType && availableEvents.includes(initialEventType);

        return (
            <InstalledBaseEventForm
                key={formResetKey}
                assetId={assetId}
                assetLocationId={assetLocationId}
                targetedNode={installedBaseNodeId ?? undefined}
                forceInstallTargetPicker={nodeTypeKey === InstalledBaseNodeTypeKey.ASSET && !nodeIsInstallTarget}
                eventType={formInitialEventType}
                selectableEventTypes={lockedEventType ? undefined : availableEvents}
                onSubmit={handleSubmit}
                onEventTypeChange={setCurrentEventType}
                relocationRootNodeId={clientRootId}
                averageRuntimePerDay={averageRuntimePerDay}
                onCancel={onClose}
                isSubmitting={submitting}
            />
        );
    };

    const titleText = currentEventType ? eventTypeLabels[currentEventType] : t('Create event');

    // Closing via the backdrop / X while the follow-up prompt is up still has to flush
    // the deferred side-effects (the event already succeeded) — treat it as "close
    // without taking a follow-up".
    const handleModalClose = () => {
        if (followUpPrompt) {
            dismissFollowUpPrompt(true);
            return;
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleModalClose} fullScreen={isFullScreen}>
            <BasicModalContent
                startIcon={currentEventType ? getEventTypeIcon(currentEventType, {}) : undefined}
                onClose={handleModalClose}
                title={<Typography level={'titleSmallLight'}>{titleText}</Typography>}
            >
                <Box sx={{ position: 'relative' }}>
                    {renderBody()}
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default CreateInstalledBaseEventModal;
